import React, { useMemo, useRef } from 'react';
import { components } from 'react-select';
import { Icon, Select, useTheme, colors, Button } from '@iq/react-components';

const DropdownIndicator = (properties) => {
  const {
    trigger,
    triggerIconColor,
    triggerThemeDarkColor,
    triggerThemeLightColor,
    theme,
    tooltip,
    triggerSize,
  } = properties.selectProps;

  const indicatorColor =
    triggerIconColor ||
    (theme === 'dark'
      ? triggerThemeDarkColor || colors.DarkApplicationPrimaryText
      : triggerThemeLightColor || colors.LightApplicationSecondaryText);

  const memoizedIndicator = useMemo(() => {
    if (typeof trigger === 'string') {
      return (
        <Button
          design="text"
          tooltip={tooltip}
          style={{ height: '1rem' }}
        >
          <Icon
            style={{ color: indicatorColor }}
            icon={trigger}
            size={triggerSize}
          />
        </Button>
      );
    }

    if (React.isValidElement(trigger)) {
      return trigger;
    }

    return <components.DownChevron />;
  }, [trigger, tooltip, indicatorColor, triggerSize]);

  return (
    <components.DropdownIndicator {...properties}>
      <div
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        {memoizedIndicator}
      </div>
    </components.DropdownIndicator>
  );
};

const DropdownMenu = ({
  trigger,
  triggerSize = 'xs',
  triggerUnderlay,
  menuOptions,
  value,
  className = '',
  tooltip = 'More...',
  isMulti = false,
  menuXPlacement = 'right',
  menuYPlacement = 'auto',
  menuShouldEscapeOverflow = false,
  onChange = (selection) => selection,
  onMenuOpen,
  onMenuClose,
  styles = {},
  fullHeight = false,
  menuIsOpen,
  ...extraProperties
}) => {
  const theme = useTheme();
  const dropdownReference = useRef();
  const menuPortalTarget = menuShouldEscapeOverflow
    ? document.querySelector('#global-modal-container') || document.body
    : undefined;

  const handleChange = (selection) => {
    if (!isMulti && selection.onSelect && typeof selection.onSelect === 'function') {
      selection.onSelect(selection);
    }
    onChange(selection);
  };

  const handleMenuClose = () => {
    if (dropdownReference.current) {
      dropdownReference.current.blur();
    }
    onMenuClose?.();
  };

  const Option = (properties) => (
    <components.Option
      {...{
        ...properties,
        innerProps: {
          ...properties.innerProps,
          onClick: (event) => {
            event.stopPropagation();
            event.preventDefault();
            properties.innerProps.onClick(event);
          },
        },
      }}
    >
      {properties.data.component || properties.data.label}
    </components.Option>
  );

  const triggerColor =
    theme === 'dark' ? colors.DarkApplicationSecondaryText : colors.LightApplicationSecondaryText;

  const dropdownStyles = {
    container: (base) => ({
      ...base,
      width: 'fit-content',
      ...styles.container,
    }),
    input: () => ({ cursor: 'none' }),
    valueContainer: () => ({ height: '0px', width: '0px', overflow: 'hidden' }),
    indicatorsContainer: (base) => ({
      ...base,
      borderRadius: '0.2rem',
      backgroundColor: triggerUnderlay || 'transparent',
      flexGrow: '1',
      color: triggerColor,
      ...styles.indicatorsContainer,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: 'pointer',
      flexGrow: '1',
      padding: trigger && React.isValidElement(trigger) ? '0px' : base.padding,
      color: triggerColor,
      '&:hover': { color: triggerColor },
      ...styles.dropdownIndicator,
    }),
    menu: (base) => ({
      ...base,
      width: 'fit-content',
      minWidth: '100%',
      // Portaled menus are already anchored to the control by react-select's own fixed positioning.
      ...(menuShouldEscapeOverflow
        ? {}
        : {
            left: menuXPlacement === 'right' ? 0 : 'auto',
            right: menuXPlacement === 'right' ? 'auto' : 0,
            top: 'unset',
          }),
      border: `1px solid ${theme === 'dark' ? colors.Grey70 : colors.Grey20}`,
      boxShadow: '0 0.5rem 0.5rem 0 rgba(0, 0, 0, 0.15)',
      zIndex: '1000',
      ...styles.menu,
    }),
    // react-select renders the portal target in a separate DOM subtree, so it needs its own z-index.
    menuPortal: (base) => ({
      ...base,
      zIndex: 2001,
      ...styles.menuPortal,
    }),
    menuList: (base) => ({
      ...base,
      ...styles.menuList,
      scrollbarColor: `${
        theme === 'dark'
          ? 'rgba(255, 255, 255, .25) rgba(0, 0, 0, 0)'
          : 'rgba(0, 0, 0, .25) rgba(0, 0, 0, 0)'
      }`,
      '::-webkit-scrollbar': {
        width: '7px',
      },
      '::-webkit-scrollbar-thumb': {
        background: `${theme === 'dark' ? 'rgba(255, 255, 255, .25)' : 'rgba(0, 0, 0, .25)'}`,
        borderRadius: '7px',
        boxShadow: `${
          theme === 'dark' ? '0 0 1px rgba(0, 0, 0, .5)' : '"0 0 1px rgba(255, 255, 255, .5)"'
        }`,
      },
    }),
    group: (base) => ({
      ...base,
      paddingTop: '0',
      paddingBottom: '0',
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme === 'dark' ? colors.Grey70 : colors.Grey20}`,
        paddingBottom: '4px',
      },
    }),
    option: (base) => ({
      ...base,
      whiteSpace: 'nowrap',
      borderRadius: '0.2rem',
      ...styles.option,
    }),
    control: (base) => ({
      ...base,
      ...(fullHeight ? {} : { minHeight: 'unset' }),
      border: 'none',
      '&:hover': { border: 'none', borderColor: 'transparent' },
      '&:active': { border: 'none', borderColor: 'transparent' },
      boxShadow: 'none',
      backgroundColor: 'transparent',
      justifyContent: 'flex-start',
      ...styles.control,
    }),
  };
  return (
    <Select
      isMulti={isMulti}
      trigger={trigger}
      options={menuOptions}
      value={value}
      className={className}
      isSearchable={false}
      onChange={handleChange}
      onMenuOpen={onMenuOpen}
      onMenuClose={handleMenuClose}
      menuPlacement={menuYPlacement}
      menuPosition={menuShouldEscapeOverflow ? 'fixed' : undefined}
      menuPortalTarget={menuPortalTarget}
      isOptionSelected={() => false}
      hideSelectedOptions={false}
      controlShouldRenderValue={false}
      components={{
        DropdownIndicator,
        Option,
        IndicatorSeparator: () => null,
        ClearIndicator: () => null,
        Placeholder: () => null,
      }}
      selectProps={{
        tooltip,
        triggerSize,
      }}
      styles={dropdownStyles}
      innerRef={dropdownReference}
      menuIsOpen={menuIsOpen}
      {...extraProperties}
    />
  );
};

export default DropdownMenu;
